import React, { useState, useRef, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import QRCode from 'react-qr-code';
import { FaCopy, FaCheck, FaDownload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as QRCodeLib from 'qrcode';
import './QRCredentialModal.scss';

interface QRCredentialModalProps {
  show: boolean;
  onHide: () => void;
  email: string;
  clientId: number;
  clientName: string;
}

const QRCredentialModal: React.FC<QRCredentialModalProps> = ({ 
  show, 
  onHide, 
  email, 
  clientId,
  clientName 
}) => {
  // Generate code from first 4 letters of name + client ID
  const namePrefix = clientName.replace(/\s+/g, '').substring(0, 4).toUpperCase();
  const qrData = `${namePrefix}-${clientId}`;
  
  const [copied, setCopied] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Generate QR code as Data URL when modal opens
  useEffect(() => {
    if (show) {
      QRCodeLib.toDataURL(qrData, {
        width: 600,
        margin: 2,
        errorCorrectionLevel: 'H',
      }).then((url: string) => {
        setQrCodeDataUrl(url);
      }).catch((err: Error) => {
        console.error('QR generation error:', err);
      });
    }
  }, [show, qrData]);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(qrData);
      setCopied(true);
      toast.success('Código copiado al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Error al copiar el código');
    }
  };

  const handleDownload = async () => {
    if (!canvasRef.current) return;

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Set canvas size
      const width = 800;
      const height = 1000;
      canvas.width = width;
      canvas.height = height;

      // White background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);

      // Draw title
      ctx.fillStyle = '#111827';
      ctx.font = 'bold 48px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Tu Credencial', width / 2, 80);

      // Draw client name
      ctx.fillStyle = '#374151';
      ctx.font = '32px Arial, sans-serif';
      ctx.fillText(clientName, width / 2, 140);

      // Draw QR code if available
      if (qrCodeDataUrl) {
        const qrImage = new Image();
        qrImage.onload = () => {
          const qrSize = 400;
          const qrX = (width - qrSize) / 2;
          const qrY = 200;

          // Draw white background for QR
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(qrX - 20, qrY - 20, qrSize + 40, qrSize + 40);

          // Draw shadow
          ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
          ctx.shadowBlur = 20;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 4;
          ctx.fillRect(qrX - 20, qrY - 20, qrSize + 40, qrSize + 40);
          ctx.shadowColor = 'transparent';

          // Draw QR code
          ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

          // Draw code label
          ctx.fillStyle = '#374151';
          ctx.font = 'bold 24px Arial, sans-serif';
          ctx.textAlign = 'left';
          const labelY = qrY + qrSize + 80;
          ctx.fillText('Código:', width / 2 - 200, labelY);

          // Draw code value
          ctx.fillStyle = '#111827';
          ctx.font = '20px "Courier New", monospace';
          ctx.fillText(qrData, width / 2 - 200, labelY + 35);

          // Draw instruction
          ctx.fillStyle = '#6B7280';
          ctx.font = '18px Arial, sans-serif';
          ctx.textAlign = 'center';
          const lines = [
            'Presenta este código QR en cualquiera',
            'de tus restaurantes favoritos'
          ];
          lines.forEach((line, index) => {
            ctx.fillText(line, width / 2, labelY + 90 + (index * 30));
          });

          // Download
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `credencial-mediopunto-${clientName.replace(/\s+/g, '-').toLowerCase()}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              toast.success('Credencial descargada exitosamente');
            }
          });
        };
        qrImage.src = qrCodeDataUrl;
      }
    } catch (error) {
      toast.error('Error al descargar la credencial');
      console.error('Download error:', error);
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered 
      className="qr-credential-modal"
    >
      <Modal.Body>
        <div className="qr-content">
          <h3 className="qr-title">Tu Credencial</h3>
          <p className="qr-name">{clientName}</p>
          
          <div className="qr-code-wrapper">
            <QRCode
              value={qrData}
              size={200}
              level="H"
              className="qr-code"
            />
          </div>

          <div className="client-code-section">
            <span className="code-label">Código:</span>
            <span className="code-value">{qrData}</span>
            <button 
              className="copy-code-btn"
              onClick={handleCopyCode}
              title="Copiar código"
            >
              {copied ? <FaCheck /> : <FaCopy />}
            </button>
          </div>
          
          <p className="qr-instruction">
            Presenta este código QR en cualquiera de tus restaurantes favoritos
          </p>
        </div>

        {/* Hidden canvas for generating download image */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        <div className="modal-actions">
          <button className="download-btn" onClick={handleDownload}>
            <FaDownload /> Descargar
          </button>
          
          <button className="close-qr-btn" onClick={onHide}>
            Cerrar
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default QRCredentialModal;
